import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import { doc, updateDoc } from "firebase/firestore";
import { db } from '../../firebaseConfig';
import SmallButton from '../shared/SmallButton';
import { sendOneTimeLoginCode, verifyCodeByTwilio, isPhoneConfirmedForAnotherUser } from '../../services/users/Users';

export default function AddPhoneNumber({ onPhoneAdded, onCancel }) {
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [isPhoneCodeSent, setIsPhoneCodeSent] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [phoneVerificationMessage, setPhoneVerificationMessage] = useState('');
  const [isVerifyingPhone, setIsVerifyingPhone] = useState(false);

  const handleAddPhoneNumber = async () => {
    if (!newPhoneNumber.trim()) {
      setPhoneVerificationMessage('Please enter a phone number.');
      return;
    }

    // Simple phone number validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    const cleanPhone = newPhoneNumber.replace(/[\s\-\(\)]/g, '');
    
    if (!phoneRegex.test(cleanPhone)) {
      setPhoneVerificationMessage('Please enter a valid phone number.');
      return;
    }

    setIsVerifyingPhone(true);
    setPhoneVerificationMessage('');
    
    try {
      // Check if phone is already confirmed for another user
      const savedUserId = window.localStorage.getItem('userId');
      const phoneAlreadyUsed = await isPhoneConfirmedForAnotherUser(cleanPhone, savedUserId);
      
      if (phoneAlreadyUsed) {
        setPhoneVerificationMessage('This phone number is already registered to another account.');
        setIsVerifyingPhone(false);
        return;
      }
      
      await sendOneTimeLoginCode(cleanPhone);
      setIsPhoneCodeSent(true);
      setPhoneVerificationMessage('');
      setIsVerifyingPhone(false); // Reset the loading state
    } catch (error) {
      setPhoneVerificationMessage('Error sending verification code. Please try again.');
      setIsVerifyingPhone(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    if (!phoneVerificationCode.trim()) {
      setPhoneVerificationMessage('Please enter the verification code.');
      return;
    }

    setIsVerifyingPhone(true);
    
    try {
      const cleanPhone = newPhoneNumber.replace(/[\s\-\(\)]/g, '');
      const result = await verifyCodeByTwilio(phoneVerificationCode, cleanPhone);
      
      if (result.success) {
        // Update user's phone number in the database
        const savedUserId = window.localStorage.getItem('userId');
        if (savedUserId) {
          const userRef = doc(db, "Users", savedUserId);
          await updateDoc(userRef, {
            phone: cleanPhone,
            isPhoneConfirmed: true,
          });
          
          setPhoneVerificationMessage('Phone number successfully added!');
          
          // Notify parent component and reset after a delay
          setTimeout(() => {
            onPhoneAdded(cleanPhone);
            resetState();
          }, 2000);
        }
      } else {
        setPhoneVerificationMessage('Incorrect verification code.');
      }
    } catch (error) {
      setPhoneVerificationMessage('Error verifying code. Please try again.');
    }
    
    setIsVerifyingPhone(false);
  };

  const handleResendPhoneCode = async () => {
    setIsVerifyingPhone(true);
    setPhoneVerificationMessage('');
    
    try {
      const cleanPhone = newPhoneNumber.replace(/[\s\-\(\)]/g, '');
      await sendOneTimeLoginCode(cleanPhone);
      setPhoneVerificationMessage('Code resent successfully!');
    } catch (error) {
      setPhoneVerificationMessage('Error resending code. Please try again.');
    }
    
    setIsVerifyingPhone(false);
  };

  const resetState = () => {
    setNewPhoneNumber('');
    setIsPhoneCodeSent(false);
    setPhoneVerificationCode('');
    setPhoneVerificationMessage('');
    setIsVerifyingPhone(false);
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  return (
    <View style={{ marginTop: 10 }}>
      {!isPhoneCodeSent ? (
        <View>
          <TextInput
            style={{ height: 40, width: 300, backgroundColor: '#fff', padding: 5, marginBottom: 10, borderWidth: 1, borderColor: '#ccc', borderRadius: 5 }}
            placeholder="Enter your phone number"
            placeholderTextColor="gray"
            value={newPhoneNumber}
            onChangeText={setNewPhoneNumber}
            textAlignVertical={"center"}
            maxLength={20}
          />
          <View style={{ flexDirection: 'row', marginBottom: 10 }}>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="basic" 
                label={isVerifyingPhone ? "Sending..." : "Save"} 
                onPress={handleAddPhoneNumber} 
                isDisabled={isVerifyingPhone}
              />
            </View>
            <SmallButton 
              theme="secondary" 
              label="Cancel" 
              onPress={handleCancel} 
            />
          </View>
        </View>
      ) : (
        <View>
          <Text style={{ fontSize: 16, marginBottom: 10 }}>Enter the verification code sent to {newPhoneNumber}:</Text>
          <TextInput
            style={{ height: 40, width: 300, backgroundColor: '#fff', padding: 5, marginBottom: 10, borderWidth: 1, borderColor: '#ccc', borderRadius: 5 }}
            placeholder="Enter verification code"
            placeholderTextColor="gray"
            value={phoneVerificationCode}
            onChangeText={setPhoneVerificationCode}
            textAlignVertical={"center"}
            maxLength={10}
          />
          <View style={{ flexDirection: 'row', marginBottom: 10 }}>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="basic" 
                label={isVerifyingPhone ? "Verifying..." : "Save"} 
                onPress={handleVerifyPhoneCode} 
                isDisabled={isVerifyingPhone}
              />
            </View>
            <View style={{ marginRight: 10 }}>
              <SmallButton 
                theme="secondary" 
                label={isVerifyingPhone ? "Sending..." : "Resend"} 
                onPress={handleResendPhoneCode} 
                isDisabled={isVerifyingPhone}
              />
            </View>
            <SmallButton 
              theme="secondary" 
              label="Cancel" 
              onPress={handleCancel} 
            />
          </View>
        </View>
      )}
      {phoneVerificationMessage ? (
        <Text style={{ color: phoneVerificationMessage.includes('success') ? '#4CAF50' : '#674FE0', marginTop: 10, fontSize: 14 }}>
          {phoneVerificationMessage}
        </Text>
      ) : null}
    </View>
  );
}